import React from 'react';
import { motion } from 'framer-motion';
import { Play, ChevronDown } from 'lucide-react';
export function HookScreen() {
  return <section className="min-h-screen flex flex-col items-center justify-center bg-white px-6 py-20 relative overflow-hidden" data-id="element-128">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
      backgroundImage: 'radial-gradient(circle at 1px 1px, #6B21A8 1px, transparent 0)',
      backgroundSize: '40px 40px'
    }} data-id="element-129" />

      <div className="max-w-6xl w-full mx-auto flex flex-col items-center relative z-10" data-id="element-130">
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-purple-600 font-semibold text-sm uppercase tracking-widest mb-6" data-id="element-131">
          The attention principle
        </motion.p>

        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 text-center mb-6 max-w-4xl leading-tight" data-id="element-132">
          Why most website experiences fail in the first{' '}
          <span className="text-purple-600" data-id="element-133">10 seconds</span>
        </motion.h1>

        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} className="text-lg text-slate-400 text-center mb-16 max-w-2xl" data-id="element-134">
          The difference between drop-off and engagement comes down to one
          thing: how much effort you demand before delivering value.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 w-full mb-16" data-id="element-135">
          {/* LEFT: Bad Example — Website screenshot */}
          <motion.div initial={{
          opacity: 0,
          x: -40
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3,
          duration: 0.6
        }} className="flex flex-col" data-id="element-136">
            <div className="relative group" data-id="element-137">
              {/* Red glow behind */}
              <div className="absolute -inset-2 bg-red-100 rounded-3xl blur-xl opacity-50" data-id="element-138" />

              <div className="w-full bg-white border-2 border-red-200 rounded-2xl shadow-lg relative overflow-hidden" data-id="element-139">
                {/* Browser chrome */}
                <div className="flex items-center gap-1.5 px-4 py-3 bg-red-50 border-b border-red-100" data-id="element-140">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-300" data-id="element-141" />
                  <div className="w-2.5 h-2.5 rounded-full bg-red-200" data-id="element-142" />
                  <div className="w-2.5 h-2.5 rounded-full bg-red-200" data-id="element-143" />
                  <div className="ml-4 h-5 w-48 bg-red-100 rounded-full" data-id="element-144" />
                </div>

                {/* Website screenshot */}
                <div className="relative" data-id="element-145">
                  <img src="https://cdn.magicpatterns.com/uploads/gFbWRNa7RhMMvjJsXxE7BT/Screenshot_2026-04-09_at_10.52.47.png" alt="Dense website with lots of text and navigation" className="w-full h-auto object-cover object-top" style={{
                  maxHeight: '360px'
                }} data-id="element-146" />
                  {/* Subtle red overlay */}
                  <motion.div animate={{
                  opacity: [0.03, 0.08, 0.03]
                }} transition={{
                  repeat: Infinity,
                  duration: 3
                }} className="absolute inset-0 bg-red-500 pointer-events-none" data-id="element-147" />
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-center justify-center gap-3" data-id="element-148">
              <div className="w-2 h-2 rounded-full bg-red-400" data-id="element-149" />
              <span className="font-semibold text-red-600 text-base" data-id="element-150">
                High effort upfront → Drop-off
              </span>
            </div>
          </motion.div>

          {/* RIGHT: Good Example — Video screenshot with play overlay */}
          <motion.div initial={{
          opacity: 0,
          x: 40
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.5,
          duration: 0.6
        }} className="flex flex-col" data-id="element-151">
            <div className="relative group" data-id="element-152">
              {/* Purple/green glow behind */}
              <div className="absolute -inset-2 bg-gradient-to-br from-purple-100 to-emerald-100 rounded-3xl blur-xl opacity-60" data-id="element-153" />

              <a href="https://player.snakker.io/s/cHonpmm3C0" target="_blank" rel="noopener noreferrer" className="block relative rounded-2xl overflow-hidden shadow-lg" data-id="element-154">
                {/* Full image with red frame visible */}
                <img src="https://cdn.magicpatterns.com/uploads/gnQ8SM99ADxWzzLrpZ1gAH/Screenshot_2026-04-09_at_11.14.29.png" alt="Clean animated video frame" className="w-full h-auto block" data-id="element-155" />

                {/* Animated pulse rings */}
                <motion.div animate={{
                scale: [1, 1.8],
                opacity: [0.3, 0]
              }} transition={{
                repeat: Infinity,
                duration: 2.5,
                ease: 'easeOut'
              }} className="absolute top-[35%] left-[42%] -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full border-2 border-white/60" data-id="element-156" />
                <motion.div animate={{
                scale: [1, 2.2],
                opacity: [0.2, 0]
              }} transition={{
                repeat: Infinity,
                duration: 2.5,
                ease: 'easeOut',
                delay: 0.5
              }} className="absolute top-[35%] left-[42%] -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full border-2 border-purple-300/60" data-id="element-157" />
                <motion.div animate={{
                scale: [1, 2.6],
                opacity: [0.15, 0]
              }} transition={{
                repeat: Infinity,
                duration: 2.5,
                ease: 'easeOut',
                delay: 1
              }} className="absolute top-[35%] left-[42%] -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full border border-white/30" data-id="element-158" />

                {/* Play button */}
                <motion.div animate={{
                scale: [1, 1.06, 1]
              }} transition={{
                repeat: Infinity,
                duration: 2.5,
                ease: 'easeInOut'
              }} className="absolute top-[35%] left-[42%] -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full bg-gradient-to-br from-purple-600 to-purple-500 shadow-xl shadow-purple-500/30 flex items-center justify-center z-10 cursor-pointer" data-id="element-159">
                  <Play size={40} className="text-white ml-2" fill="white" data-id="element-160" />
                </motion.div>
              </a>
            </div>

            <div className="mt-8 flex items-center justify-center gap-3" data-id="element-161">
              <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-162" />
              <span className="font-semibold text-purple-700 text-base" data-id="element-163">
                Low effort → Instant clarity
              </span>
            </div>
          </motion.div>
        </div>

        <motion.p initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 1
      }} className="text-xl text-slate-500 text-center font-medium max-w-lg" data-id="element-164">
          People don't invest effort until they see value
        </motion.p>
      </div>

      <motion.div animate={{
      y: [0, 8, 0]
    }} transition={{
      repeat: Infinity,
      duration: 2,
      ease: 'easeInOut'
    }} className="absolute bottom-10 text-purple-300" data-id="element-165">
        <ChevronDown size={28} strokeWidth={1.5} data-id="element-166" />
      </motion.div>
    </section>;
}